import React, {useState, useEffect, useCallback} from 'react';

import {generalStore, modalStore} from '../../../index';
import {observer} from 'mobx-react';
import PresetListView from './PresetListView';
import ManualRoleView from './ManualRoleView';
import useRoleSelection from './useRoleSelection';

const closePopup = () => {
    modalStore.isChooseRolePopupOpen = false;
    modalStore.chooseRolePopupPosition = {x: 100, y: 500};
    modalStore.handleSaveRolePopup = () => {};
    modalStore.chooseInitRoleState = {contentCreating: new Set(), trainingAndField: new Set(), administration: new Set()};
    modalStore.chooseRolePopupConfig = {singleSelect: false, showAdministration: false};
};

export const ChooseRolePopup = observer(() => {
    const config = modalStore.chooseRolePopupConfig || {};
    const showAdministration = config.showAdministration || false;
    const isFilterMode = config.isFilterMode || false;
    const showPresetActions = !!config.showPresetActions;

    const isOpen = modalStore.isChooseRolePopupOpen;
    const {training, content, admin, getSelection, setSelection, setTraining, setContent, setAdmin} = useRoleSelection({
        isSingleSelect: config.singleSelect || false,
        isFilterMode,
        isOpen,
    });

    const [view, setView] = useState('manual');
    const [isBusy, setIsBusy] = useState(false);

    useEffect(() => {
        if (isOpen) setView(config.initialView || 'manual');
    }, [isOpen]);

    const handleUseTemplate = useCallback((preset) => {
        setTraining(new Set([preset.TrainingAndFieldRole]));
        setContent(new Set([preset.ContentCreationRole]));
        setAdmin(new Set([preset.AdministrationRole]));
        setView('manual');
    }, [setTraining, setContent, setAdmin]);

    const handlePresetAssign = useCallback(async (preset) => {
        if (!config.onPresetAssign || isBusy) return;
        setIsBusy(true);
        closePopup();
        try {
            await config.onPresetAssign(preset);
        } finally {
            setIsBusy(false);
        }
    }, [config, isBusy]);

    const handleFilterApply = useCallback((presets) => {
        if (config.onFilterApply) {
            config.onFilterApply(presets);
            closePopup();
        }
    }, [config]);

    const getPresetOnSelect = () => {
        if (isFilterMode) return null;
        if (view === 'useTemplate') return handleUseTemplate;
        if (config.onPresetAssign) return handlePresetAssign;
        return handleUseTemplate;
    };

    if (!isOpen || isBusy) return null;

    const isPresetView = view === 'presets' || view === 'useTemplate';

    return (
        <>
            <div className="fixed inset-0 z-[1999]" onClick={closePopup} />
            <div
                className="absolute flex flex-col gap-3 p-4 rounded-lg bg-background-normal text-main-normal"
                style={{
                    width: 320,
                    maxHeight: 600,
                    boxShadow: '0 4px 12.5px 0 rgba(0, 0, 0, 0.25)',
                    zIndex: 2000,
                    ...(modalStore.chooseRolePopupPosition.anchorBottom
                        ? {bottom: `${window.innerHeight - modalStore.chooseRolePopupPosition.y}px`}
                        : {top: `${modalStore.chooseRolePopupPosition.y}px`}),
                    left: `${modalStore.chooseRolePopupPosition.x}px`,
                }}
            >
                {isPresetView ? (
                    <PresetListView
                        onSelect={getPresetOnSelect()}
                        onFilterApply={isFilterMode ? handleFilterApply : null}
                        onSwitchToManual={() => setView('manual')}
                        onClose={closePopup}
                    />
                ) : (
                    <ManualRoleView
                        training={training}
                        content={content}
                        admin={admin}
                        getSelection={getSelection}
                        setSelection={setSelection}
                        showAdministration={showAdministration}
                        isFilterMode={isFilterMode}
                        showPresetActions={showPresetActions}
                        config={config}
                        onSwitchToPresets={() => setView('presets')}
                        onUseTemplate={() => setView('useTemplate')}
                        onClose={closePopup}
                    />
                )}
            </div>
        </>
    );
});
